/**
 * Per-trade text for the /demo fixtures that read as plumbing no matter which
 * invite opened the tour (fnd-w8d) — reviews, bookings, notifications, the
 * message thread, and the content plan. `tradeJobs.ts` already swaps lead
 * *titles* by trade; this does the same for the surrounding copy so a
 * landscaping or roofing invite doesn't land on "Sump pump gave out" reviews
 * or a "water heater replacement" content-plan post.
 *
 * Each table is indexed in parallel with the corresponding static fixture
 * array (`fixtures/reviews.ts`, `fixtures/bookings.ts`, etc.) — same length,
 * same order, same reviewer/customer names, ratings, statuses and dates.
 * Only the trade-specific prose changes. The `plumbing` entry reproduces the
 * original fixture text (with the business name templated instead of
 * hard-coded "Republic"), so the plain `/demo` fixture renders byte-for-byte
 * what it always has.
 */

import type { TradeKey } from './tradeJobs';

/* ------------------------------------------------------------------ */
/* Reviews — 5 entries, parallel to fixtures/reviews.ts `demoReviews`.  */
/* ------------------------------------------------------------------ */

export interface ReviewCopyEntry {
  reviewText: (business: string) => string;
  response: ((business: string) => string) | null;
}

export const REVIEW_COPY: Record<TradeKey, ReviewCopyEntry[]> = {
  plumbing: [
    {
      reviewText: business =>
        `Sump pump gave out in the middle of a storm and ${business} had someone at the house within two hours. Explained what failed, showed me the old check valve, cleaned up after. Straight price, no upsell.`,
      response: () =>
        'Thanks Elaine — glad we caught it before the water got any higher. Give us a shout if the new pump ever sounds off.',
    },
    {
      reviewText: () =>
        'Split spigot from the freeze. Quoted over the phone, came out the next morning and the price matched the quote exactly.',
      response: () => 'Appreciate the review, Hector — and thanks for sending your neighbour our way.',
    },
    {
      reviewText: () =>
        'Good work on the laundry hookups. Only reason for four stars is that the first appointment got moved, but they called ahead and were honest about why.',
      response: null,
    },
    {
      reviewText: () => 'Replaced a leaking shut-off under the kitchen sink in about twenty minutes. Tidy, polite, fair.',
      response: () => 'Thank you, Jerome. Those little valves fail quietly — good that you spotted it.',
    },
    {
      reviewText: business =>
        `We had three bids for a water heater. ${business} was the only one that actually looked at the vent before quoting.`,
      response: null,
    },
  ],
  hvac: [
    {
      reviewText: business =>
        `The furnace quit in the middle of a cold snap and ${business} had someone at the house within two hours. Explained what failed, showed me the old igniter, cleaned up after. Straight price, no upsell.`,
      response: () =>
        'Thanks Elaine — glad we caught it before the house got any colder. Give us a shout if the furnace ever sounds off again.',
    },
    {
      reviewText: () =>
        'AC quit during the heat wave. Quoted over the phone, came out the next morning and the price matched the quote exactly.',
      response: () => 'Appreciate the review, Hector — and thanks for sending your neighbour our way.',
    },
    {
      reviewText: () =>
        'Good work on the new thermostat wiring. Only reason for four stars is that the first appointment got moved, but they called ahead and were honest about why.',
      response: null,
    },
    {
      reviewText: () => 'Replaced a failing capacitor on the AC unit in about twenty minutes. Tidy, polite, fair.',
      response: () => 'Thank you, Jerome. Those little parts fail quietly — good that you spotted it.',
    },
    {
      reviewText: business =>
        `We had three bids for a new furnace. ${business} was the only one that actually measured the ductwork before quoting.`,
      response: null,
    },
  ],
  roofing: [
    {
      reviewText: business =>
        `Shingles came off in the middle of a storm and ${business} had someone out within two hours. Tarped the roof, showed me the damage, cleaned up after. Straight price, no upsell.`,
      response: () =>
        'Thanks Elaine — glad we got the tarp up before more rain got in. Give us a shout if it ever leaks again.',
    },
    {
      reviewText: () =>
        'Roof started leaking during the freeze. Quoted over the phone, came out the next morning and the price matched the quote exactly.',
      response: () => 'Appreciate the review, Hector — and thanks for sending your neighbour our way.',
    },
    {
      reviewText: () =>
        'Good work on the gutter replacement. Only reason for four stars is that the first appointment got moved, but they called ahead and were honest about why.',
      response: null,
    },
    {
      reviewText: () => 'Fixed a flashing leak around the chimney in about twenty minutes. Tidy, polite, fair.',
      response: () => 'Thank you, Jerome. Those little leaks add up — good that you spotted it.',
    },
    {
      reviewText: business =>
        `We had three bids for a full roof replacement. ${business} was the only one that actually got on the roof before quoting.`,
      response: null,
    },
  ],
  electrical: [
    {
      reviewText: business =>
        `The power went out mid-storm and ${business} had someone out within two hours. Found the tripped breaker, showed me what failed, cleaned up after. Straight price, no upsell.`,
      response: () =>
        'Thanks Elaine — glad we caught it before anything else tripped. Give us a shout if that breaker ever acts up again.',
    },
    {
      reviewText: () =>
        'Outlet started sparking after the freeze. Quoted over the phone, came out the next morning and the price matched the quote exactly.',
      response: () => 'Appreciate the review, Hector — and thanks for sending your neighbour our way.',
    },
    {
      reviewText: () =>
        'Good work on the new panel wiring. Only reason for four stars is that the first appointment got moved, but they called ahead and were honest about why.',
      response: null,
    },
    {
      reviewText: () => 'Replaced a failing outlet in the kitchen in about twenty minutes. Tidy, polite, fair.',
      response: () => 'Thank you, Jerome. Those little outlets fail quietly — good that you spotted it.',
    },
    {
      reviewText: business =>
        `We had three bids for a panel upgrade. ${business} was the only one that actually opened the panel before quoting.`,
      response: null,
    },
  ],
  landscaping: [
    {
      reviewText: business =>
        `The irrigation system failed in the middle of a heat wave and ${business} had someone out within two hours. Explained what failed, showed me the broken valve, cleaned up after. Straight price, no upsell.`,
      response: () =>
        'Thanks Elaine — glad we caught it before the lawn took more damage. Give us a shout if that valve ever acts up again.',
    },
    {
      reviewText: () =>
        'Sprinkler line broke after the freeze. Quoted over the phone, came out the next morning and the price matched the quote exactly.',
      response: () => 'Appreciate the review, Hector — and thanks for sending your neighbour our way.',
    },
    {
      reviewText: () =>
        'Good work on the mulching and edging. Only reason for four stars is that the first appointment got moved, but they called ahead and were honest about why.',
      response: null,
    },
    {
      reviewText: () => 'Cleared a clogged irrigation valve in about twenty minutes. Tidy, polite, fair.',
      response: () => 'Thank you, Jerome. Those little valves fail quietly — good that you spotted it.',
    },
    {
      reviewText: business =>
        `We had three bids for a full yard renovation. ${business} was the only one that actually walked the whole property before quoting.`,
      response: null,
    },
  ],
  cleaning: [
    {
      reviewText: business =>
        `Needed an emergency move-out clean with two days' notice and ${business} fit us in and had the place spotless by the deadline. Explained the plan up front, no upsell.`,
      response: () => 'Thanks Elaine — glad it worked out in time. Give us a shout if you ever need another rush job.',
    },
    {
      reviewText: () =>
        'Booked a one-time deep clean before a family visit. Quoted over the phone, showed up the next morning and the price matched the quote exactly.',
      response: () => 'Appreciate the review, Hector — and thanks for sending your neighbour our way.',
    },
    {
      reviewText: () =>
        'Good work on the post-construction clean. Only reason for four stars is that the first appointment got moved, but they called ahead and were honest about why.',
      response: null,
    },
    {
      reviewText: () => 'Did a full carpet shampoo in about twenty minutes per room. Tidy, polite, fair.',
      response: () => 'Thank you, Jerome. Good catch flagging that room — it needed the extra pass.',
    },
    {
      reviewText: business =>
        `We had three bids for a recurring biweekly clean. ${business} was the only one that actually walked the house before quoting.`,
      response: null,
    },
  ],
  generic: [
    {
      reviewText: business =>
        `Had an urgent situation come up and ${business} had someone out within two hours. Explained what they found, cleaned up after. Straight price, no upsell.`,
      response: () => 'Thanks Elaine — glad we got there in time. Give us a shout if anything else comes up.',
    },
    {
      reviewText: () =>
        'Needed same-day service after something broke. Quoted over the phone, came out the next morning and the price matched the quote exactly.',
      response: () => 'Appreciate the review, Hector — and thanks for sending your neighbour our way.',
    },
    {
      reviewText: () =>
        'Good work overall. Only reason for four stars is that the first appointment got moved, but they called ahead and were honest about why.',
      response: null,
    },
    {
      reviewText: () => 'Handled the job in about twenty minutes. Tidy, polite, fair.',
      response: () => 'Thank you, Jerome. Good that you spotted it early.',
    },
    {
      reviewText: business =>
        `We had three bids for the job. ${business} was the only one that actually looked closely before quoting.`,
      response: null,
    },
  ],
};

/* ------------------------------------------------------------------ */
/* Bookings — 3 entries, parallel to fixtures/bookings.ts.             */
/* ------------------------------------------------------------------ */

export interface BookingCopyEntry {
  serviceType: string;
  notes: string;
}

export const BOOKING_COPY: Record<TradeKey, BookingCopyEntry[]> = {
  plumbing: [
    { serviceType: 'Drain cleaning', notes: 'Kitchen sink, both basins slow. Park in the alley.' },
    { serviceType: 'Water heater estimate', notes: 'Bring the 50-gallon spec sheet.' },
    {
      serviceType: 'Remodel rough-in walkthrough',
      notes: '1948 house, hall bathroom. Contractor will be on site.',
    },
  ],
  hvac: [
    { serviceType: 'Furnace tune-up', notes: 'Attic unit, hard to access. Park in the alley.' },
    { serviceType: 'AC replacement estimate', notes: 'Bring the 3-ton spec sheet.' },
    {
      serviceType: 'Ductless mini-split walkthrough',
      notes: '1948 house, no existing ductwork. Contractor will be on site.',
    },
  ],
  roofing: [
    { serviceType: 'Gutter cleaning', notes: 'Two-story, steep pitch. Park in the alley.' },
    { serviceType: 'Roof replacement estimate', notes: 'Bring the shingle sample book.' },
    {
      serviceType: 'Storm damage walkthrough',
      notes: '1948 house, hail damage on the north side. Insurance adjuster will be on site.',
    },
  ],
  electrical: [
    { serviceType: 'Outlet troubleshooting', notes: 'Kitchen circuit keeps tripping. Park in the alley.' },
    { serviceType: 'Panel upgrade estimate', notes: 'Bring the 200-amp spec sheet.' },
    {
      serviceType: 'Whole-house rewire walkthrough',
      notes: '1948 house, original knob-and-tube. Contractor will be on site.',
    },
  ],
  landscaping: [
    { serviceType: 'Lawn maintenance visit', notes: 'Backyard only this week. Park in the alley.' },
    { serviceType: 'Irrigation system estimate', notes: 'Bring the zone layout sheet.' },
    {
      serviceType: 'Hardscape walkthrough',
      notes: 'Corner lot, sloped backyard. Contractor will be on site.',
    },
  ],
  cleaning: [
    { serviceType: 'Recurring biweekly clean', notes: 'Kitchen and bathrooms priority. Park in the alley.' },
    { serviceType: 'Move-out clean estimate', notes: 'Bring the checklist for the walkthrough.' },
    {
      serviceType: 'Post-construction clean walkthrough',
      notes: 'New build, dust everywhere. Contractor will be on site.',
    },
  ],
  generic: [
    { serviceType: 'Service visit', notes: 'Details in the intake form. Park in the alley.' },
    { serviceType: 'Estimate', notes: 'Bring the standard quote sheet.' },
    { serviceType: 'Project walkthrough', notes: 'Site visit before quoting. Contractor will be on site.' },
  ],
};

/* ------------------------------------------------------------------ */
/* Notifications — the two lead-message strings and the "site         */
/* updated" line, parallel to fixtures/notifications.ts.               */
/* ------------------------------------------------------------------ */

export interface NotificationCopyEntry {
  leadMsg1: string;
  leadMsg2: string;
  systemMsg: string;
}

export const NOTIFICATION_COPY: Record<TradeKey, NotificationCopyEntry> = {
  plumbing: {
    leadMsg1: 'Emergency repair request from the contact form.',
    leadMsg2: 'Water heater replacement quote request.',
    systemMsg: 'Your water heater service page went live.',
  },
  hvac: {
    leadMsg1: 'No heat call from the contact form.',
    leadMsg2: 'AC replacement quote request.',
    systemMsg: 'Your furnace tune-up service page went live.',
  },
  roofing: {
    leadMsg1: 'Storm damage inspection request from the contact form.',
    leadMsg2: 'Roof replacement quote request.',
    systemMsg: 'Your roof leak repair service page went live.',
  },
  electrical: {
    leadMsg1: 'Panel upgrade request from the contact form.',
    leadMsg2: 'EV charger install quote request.',
    systemMsg: 'Your panel upgrade service page went live.',
  },
  landscaping: {
    leadMsg1: 'Spring cleanup request from the contact form.',
    leadMsg2: 'Irrigation repair quote request.',
    systemMsg: 'Your lawn maintenance service page went live.',
  },
  cleaning: {
    leadMsg1: 'One-time deep clean request from the contact form.',
    leadMsg2: 'Recurring biweekly clean quote request.',
    systemMsg: 'Your move-out clean service page went live.',
  },
  generic: {
    leadMsg1: 'New service request from the contact form.',
    leadMsg2: 'Quote request received.',
    systemMsg: 'Your service page went live.',
  },
};

/* ------------------------------------------------------------------ */
/* Message thread — the one customer/team exchange, parallel to        */
/* fixtures/messages.ts.                                               */
/* ------------------------------------------------------------------ */

export interface MessageCopyEntry {
  customer: string;
  team: string;
}

export const MESSAGE_COPY: Record<TradeKey, MessageCopyEntry> = {
  plumbing: {
    customer: 'Can we add the after-hours rate to the emergency page? People keep ringing to ask.',
    team:
      'Added it under the call-out box and put the same line in the FAQ. Have a look and tell us if the wording is right.',
  },
  hvac: {
    customer: 'Can we add the after-hours rate to the no-heat page? People keep ringing to ask.',
    team:
      'Added it under the call-out box and put the same line in the FAQ. Have a look and tell us if the wording is right.',
  },
  roofing: {
    customer: 'Can we add the after-hours rate to the storm damage page? People keep ringing to ask.',
    team:
      'Added it under the call-out box and put the same line in the FAQ. Have a look and tell us if the wording is right.',
  },
  electrical: {
    customer: 'Can we add the after-hours rate to the emergency page? People keep ringing to ask.',
    team:
      'Added it under the call-out box and put the same line in the FAQ. Have a look and tell us if the wording is right.',
  },
  landscaping: {
    customer: 'Can we add the spring cleanup pricing to the services page? People keep asking before they call.',
    team:
      'Added it under the pricing section and put the same line in the FAQ. Have a look and tell us if the wording is right.',
  },
  cleaning: {
    customer: 'Can we add the move-out clean pricing to the services page? People keep asking before they call.',
    team:
      'Added it under the pricing section and put the same line in the FAQ. Have a look and tell us if the wording is right.',
  },
  generic: {
    customer: 'Can we add pricing to the services page? People keep asking before they call.',
    team:
      'Added it under the pricing section and put the same line in the FAQ. Have a look and tell us if the wording is right.',
  },
};

/* ------------------------------------------------------------------ */
/* Content plan — 5 entries (blog, social, blog, faq, gallery),        */
/* parallel to fixtures/contentPlan.ts. `action` is only overridden    */
/* on the faq entry (index 3) — the gallery reminder's action text is  */
/* trade-neutral already.                                              */
/* ------------------------------------------------------------------ */

export interface ContentCopyEntry {
  title: string;
  content: string;
  keywords: string[];
  action?: string;
}

export const CONTENT_COPY: Record<TradeKey, ContentCopyEntry[]> = {
  plumbing: [
    {
      title: 'How to shut your water off before the plumber arrives',
      content:
        'A short guide for homeowners: where the main shut-off usually sits in older houses, how to tell a stuck valve from a seized one, and what to do if the handle snaps. Written to be the page someone finds at 11pm with water on the floor.',
      keywords: ['water shut off valve', 'emergency plumber'],
    },
    {
      title: 'Before and after: 1948 basement repipe',
      content:
        'Three photos from the Ridgway job — galvanised out, PEX in, and the finished manifold. Caption names the neighbourhood and the crew.',
      keywords: ['repipe', 'plumber'],
    },
    {
      title: 'What a water heater replacement actually costs',
      content:
        'Honest price bands for 40 and 50 gallon gas units, what drives the difference (venting, expansion tank, code updates), and when a repair is still the better call.',
      keywords: ['water heater replacement cost', 'water heater price'],
    },
    {
      title: 'Do you charge for after-hours calls?',
      content: 'Answer the emergency-rate question plainly on the site so nobody has to ring to find out.',
      keywords: ['after hours plumber', 'emergency rates'],
      action: 'Confirm the current after-hours rate before this goes live.',
    },
    {
      title: 'Add five job photos from the last month',
      content:
        'Sump pump swap, the spigot repair and the Ridgway rough-in. Real photos beat stock on every service page.',
      keywords: [],
    },
  ],
  hvac: [
    {
      title: 'How to reset your furnace before calling for a no-heat visit',
      content:
        'A short guide for homeowners: where the reset switch usually is, how to tell a tripped safety switch from a dead igniter, and what to do if it still won\'t start. Written to be the page someone finds at 11pm with no heat.',
      keywords: ['furnace reset', 'no heat repair'],
    },
    {
      title: 'Before and after: ductless mini-split install',
      content:
        'Three photos from the Ridgway job — old baseboard heat out, new mini-split head in, and the finished wall plate. Caption names the neighbourhood and the crew.',
      keywords: ['ductless mini split', 'hvac install'],
    },
    {
      title: 'What a new furnace actually costs',
      content:
        'Honest price bands for single-stage and two-stage gas furnaces, what drives the difference (ductwork, permits, efficiency rating), and when a repair is still the better call.',
      keywords: ['furnace replacement cost', 'new furnace price'],
    },
    {
      title: 'Do you charge for after-hours calls?',
      content: 'Answer the emergency-rate question plainly on the site so nobody has to ring to find out.',
      keywords: ['after hours hvac', 'emergency rates'],
      action: 'Confirm the current after-hours rate before this goes live.',
    },
    {
      title: 'Add five job photos from the last month',
      content:
        'Furnace swap, the mini-split install and the Ridgway ductwork job. Real photos beat stock on every service page.',
      keywords: [],
    },
  ],
  roofing: [
    {
      title: 'How to check your attic for a roof leak before calling',
      content:
        'A short guide for homeowners: where leaks usually show up first, how to tell a flashing leak from a shingle leak, and what to do if you find a wet spot. Written to be the page someone finds at 11pm with water coming through the ceiling.',
      keywords: ['roof leak check', 'emergency roof repair'],
    },
    {
      title: 'Before and after: full roof tear-off',
      content:
        'Three photos from the Ridgway job — old shingles off, new underlayment down, and the finished ridge line. Caption names the neighbourhood and the crew.',
      keywords: ['roof replacement', 'roofing contractor'],
    },
    {
      title: 'What a roof replacement actually costs',
      content:
        'Honest price bands for asphalt shingle roofs by square footage, what drives the difference (pitch, layers to remove, decking repair), and when a repair is still the better call.',
      keywords: ['roof replacement cost', 'new roof price'],
    },
    {
      title: 'Do you charge for after-hours storm calls?',
      content: 'Answer the emergency-rate question plainly on the site so nobody has to ring to find out.',
      keywords: ['after hours roofer', 'emergency rates'],
      action: 'Confirm the current after-hours rate before this goes live.',
    },
    {
      title: 'Add five job photos from the last month',
      content:
        'The storm damage inspection, the gutter install and the Ridgway tear-off. Real photos beat stock on every service page.',
      keywords: [],
    },
  ],
  electrical: [
    {
      title: 'How to reset a breaker before calling an electrician',
      content:
        'A short guide for homeowners: where the panel usually sits, how to tell a tripped breaker from a dead outlet, and what to do if it trips again right away. Written to be the page someone finds at 11pm with the power out.',
      keywords: ['breaker reset', 'emergency electrician'],
    },
    {
      title: 'Before and after: 1948 house rewire',
      content:
        'Three photos from the Ridgway job — knob-and-tube out, new copper wiring in, and the finished panel. Caption names the neighbourhood and the crew.',
      keywords: ['rewire', 'electrician'],
    },
    {
      title: 'What a panel upgrade actually costs',
      content:
        'Honest price bands for 100 and 200 amp panel upgrades, what drives the difference (permits, meter work, code updates), and when a repair is still the better call.',
      keywords: ['panel upgrade cost', 'electrical panel price'],
    },
    {
      title: 'Do you charge for after-hours calls?',
      content: 'Answer the emergency-rate question plainly on the site so nobody has to ring to find out.',
      keywords: ['after hours electrician', 'emergency rates'],
      action: 'Confirm the current after-hours rate before this goes live.',
    },
    {
      title: 'Add five job photos from the last month',
      content:
        'The panel upgrade, the outlet install and the Ridgway rewire. Real photos beat stock on every service page.',
      keywords: [],
    },
  ],
  landscaping: [
    {
      title: 'How to spot an irrigation leak before it shows up on your bill',
      content:
        'A short guide for homeowners: where leaks usually start, how to tell a broken head from a cracked line, and what to do if a zone won\'t shut off. Written to be the page someone finds staring at a soggy lawn.',
      keywords: ['irrigation leak', 'lawn care'],
    },
    {
      title: 'Before and after: full yard renovation',
      content:
        'Three photos from the Ridgway job — old sod out, new sod and beds in, and the finished walkway. Caption names the neighbourhood and the crew.',
      keywords: ['landscaping', 'yard renovation'],
    },
    {
      title: 'What a full yard renovation actually costs',
      content:
        'Honest price bands for sod, mulch beds, and hardscape by square footage, what drives the difference (grading, materials, access), and when a smaller project is still the better call.',
      keywords: ['landscaping cost', 'yard renovation price'],
    },
    {
      title: 'Do you charge extra for same-week scheduling?',
      content: 'Answer the rush-scheduling question plainly on the site so nobody has to ring to find out.',
      keywords: ['landscaper scheduling', 'rush fee'],
      action: 'Confirm the current rush-scheduling fee before this goes live.',
    },
    {
      title: 'Add five job photos from the last month',
      content:
        'The spring cleanup, the irrigation repair and the Ridgway yard renovation. Real photos beat stock on every service page.',
      keywords: [],
    },
  ],
  cleaning: [
    {
      title: 'What to do before your first deep clean',
      content:
        'A short guide for homeowners: what to clear off counters, whether pets need to be put away, and what a first deep clean actually covers versus a recurring visit. Written to be the page someone finds the night before their first appointment.',
      keywords: ['deep clean checklist', 'first time cleaning service'],
    },
    {
      title: 'Before and after: post-construction clean',
      content:
        'Three photos from the Ridgway job — dust and debris out, floors and counters shining, and the finished walkthrough. Caption names the neighbourhood and the crew.',
      keywords: ['post construction clean', 'cleaning service'],
    },
    {
      title: 'What a recurring clean actually costs',
      content:
        'Honest price bands for weekly and biweekly cleans by home size, what drives the difference (pets, add-ons, first-clean deep work), and when a one-time clean is still the better call.',
      keywords: ['cleaning service cost', 'recurring clean price'],
    },
    {
      title: 'Do you charge extra for a first-time deep clean?',
      content: 'Answer the first-clean pricing question plainly on the site so nobody has to ring to find out.',
      keywords: ['cleaning service pricing', 'first clean rate'],
      action: 'Confirm the current first-clean rate before this goes live.',
    },
    {
      title: 'Add five job photos from the last month',
      content:
        'The move-out clean, the window cleaning job and the Ridgway post-construction clean. Real photos beat stock on every service page.',
      keywords: [],
    },
  ],
  generic: [
    {
      title: 'What to expect before your first appointment',
      content:
        'A short guide for homeowners: what to have ready, how scheduling works, and what a first visit actually covers. Written to be the page someone finds the night before their appointment.',
      keywords: ['first appointment', 'service checklist'],
    },
    {
      title: 'Before and after: a recent job',
      content:
        'Three photos from a recent job — before, in progress, and finished. Caption names the neighbourhood and the crew.',
      keywords: ['before and after', 'local service'],
    },
    {
      title: 'What this kind of job actually costs',
      content:
        'Honest price bands for the most common jobs, what drives the difference, and when a smaller visit is still the better call.',
      keywords: ['service cost', 'pricing'],
    },
    {
      title: 'Do you charge extra for rush scheduling?',
      content: 'Answer the rush-scheduling question plainly on the site so nobody has to ring to find out.',
      keywords: ['rush scheduling', 'service pricing'],
      action: 'Confirm the current rush fee before this goes live.',
    },
    {
      title: 'Add five job photos from the last month',
      content: 'Recent jobs from the last month. Real photos beat stock on every service page.',
      keywords: [],
    },
  ],
};
